import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, GitCompareArrows, Share2 } from "lucide-react";
import BottleSilhouette from "../components/BottleSilhouette";
import GinImage from "../components/GinImage";
import DnaRadar from "../components/DnaRadar";
import DnaBars from "../components/DnaBars";
import { getGin, getRelated } from "../lib/api";
import { DNA_AXES, getCategoryColor } from "../lib/constants";
import { recordEvent } from "../lib/adminApi";

export default function GinDetail() {
  const { id } = useParams();
  const [gin, setGin] = useState(null);
  const [related, setRelated] = useState({ similar: [], upgrade: [], safe: [], wildcard: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setGin(null);
    Promise.all([getGin(id), getRelated(id)])
      .then(([g, r]) => { setGin(g); setRelated(r); setLoading(false); recordEvent("gin_view", { gin_id: id }); })
      .catch(() => setLoading(false));
  }, [id]);

  if (loading || !gin) {
    return <div className="px-6 py-32 text-center text-white/50">Pouring the gin...</div>;
  }

  const color = getCategoryColor(gin.category);
  const axes = DNA_AXES.map((a) => ({ ...a, value: gin.dna[a.key] || 0 }));

  const unavailable = gin.availability_status === "NOT_AVAILABLE" || gin.is_available === false;

  return (
    <div className="pb-24" data-testid={`gin-detail-${gin.id}`}>
      {/* Unavailable banner */}
      {unavailable && (
        <div
          className="bg-[#15171A] border-y border-[#C17F59]/30 px-5 md:px-12 py-4 max-w-7xl mx-auto"
          data-testid="gin-unavailable-banner"
        >
          <div className="flex items-start gap-3">
            <span className="overline text-[#C17F59] mt-1 shrink-0">Stock note</span>
            <div>
              <p className="font-display text-lg md:text-xl leading-tight">Currently not available at BENO.</p>
              <p className="text-sm text-white/65 italic mt-1">
                Ask the bartender for a similar available gin — they'll guide you to something close.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Back nav */}
      <div className="px-5 md:px-12 max-w-7xl mx-auto pt-4">
        <Link to="/gin/explore" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/50 hover:text-[#C17F59] transition-colors" data-testid="back-explore">
          <ArrowLeft size={14} /> Back to Atlas
        </Link>
      </div>

      {/* HERO */}
      <section className="px-5 md:px-12 max-w-7xl mx-auto pt-8 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          {/* Bottle */}
          <div className="md:col-span-5">
            <div className="relative bg-[#15171A] border border-white/8 p-10 grain flex items-center justify-center min-h-[420px]">
              <div className="absolute inset-0"
                   style={{ background: `radial-gradient(circle at 50% 60%, ${color}22, transparent 75%)` }} />
              <motion.div initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
                <GinImage gin={gin} size={160} />
              </motion.div>
            </div>
          </div>

          {/* Info */}
          <div className="md:col-span-7 space-y-5">
            <p className="overline text-[#C17F59] flex items-center gap-3">
              <span>{gin.country}</span>
              <span className="h-px w-8 bg-[#C17F59]/40" />
              <span>{gin.styles.slice(0, 2).join(" · ")}</span>
            </p>
            <h1 className="font-display text-5xl sm:text-6xl leading-[0.95] tracking-tight" data-testid="gin-name">{gin.name}</h1>
            <p className="text-lg text-white/75 italic font-display max-w-2xl">"{gin.hook}"</p>
            <div className="flex flex-wrap items-center gap-x-8 gap-y-2 pt-2 text-sm">
              <div><span className="overline text-white/40">Price · 30ml</span><div className="font-display text-2xl">₹{gin.price}</div></div>
              <div><span className="overline text-white/40">ABV</span><div className="font-display text-2xl">{gin.abv}%</div></div>
              <div><span className="overline text-white/40">Best Serve</span><div className="font-display text-2xl">{gin.best_serves[0]}</div></div>
            </div>

            <div className="flex flex-wrap gap-1.5 pt-2">
              {gin.moods.map((m) => (
                <span key={m} className="text-[10px] uppercase tracking-[0.18em] text-white/60 border border-white/10 px-2 py-1">{m}</span>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-3">
              {!unavailable && <CompareButton ginId={gin.id} />}
              <button onClick={() => { navigator.share?.({ title: gin.name, text: gin.hook, url: window.location.href }); }}
                      className="inline-flex items-center gap-2 border border-white/15 px-4 py-3 text-[11px] uppercase tracking-[0.25em] hover:border-[#C17F59] hover:text-[#C17F59] transition-colors"
                      data-testid="share-button">
                <Share2 size={14} /> Share
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* DNA SECTION */}
      <section className="px-5 md:px-12 max-w-7xl mx-auto py-16 border-t border-white/5" data-testid="dna-section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5 space-y-4">
            <p className="overline text-[#C17F59]">BENO Gin DNA</p>
            <h2 className="font-display text-4xl">A flavor map, in ten lines.</h2>
            <p className="text-white/65 leading-relaxed">An in-house flavor read across ten axes. Not a lab test — a guide from people who pour this gin every night.</p>
            <DnaBars axes={axes} category={gin.category} />
          </div>
          <div className="md:col-span-7 bg-[#15171A] border border-white/8 p-6">
            <DnaRadar items={[{ name: gin.name, dna: gin.dna, category: gin.category }]} height={420} />
          </div>
        </div>
      </section>

      {/* FLAVOR NOTES */}
      <section className="px-5 md:px-12 max-w-7xl mx-auto py-16 border-t border-white/5" data-testid="flavor-notes">
        <p className="overline text-[#C17F59] mb-2">Flavor Notes</p>
        <h2 className="font-display text-4xl mb-8">Nose, palate, finish.</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[{ k: "nose", l: "Nose" }, { k: "palate", l: "Palate" }, { k: "finish", l: "Finish" }].map(({ k, l }) => (
            <div key={k}>
              <p className="overline text-white/40 mb-3">{l}</p>
              <p className="font-display text-2xl leading-snug">{gin[k]}</p>
            </div>
          ))}
        </div>
      </section>

      {/* BOTANICALS */}
      <section className="px-5 md:px-12 max-w-7xl mx-auto py-16 border-t border-white/5" data-testid="botanicals-section">
        <p className="overline text-[#C17F59] mb-2">Dominant Botanicals</p>
        <h2 className="font-display text-4xl mb-8">What's in the still.</h2>
        <div className="flex flex-wrap gap-2">
          {gin.botanicals.map((b, i) => (
            <motion.span
              key={b}
              initial={{ scale: 0.7, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: i * 0.04 }}
              className="bloom-in px-3 py-2 border border-[#C17F59]/30 text-sm font-display tracking-wide bg-[#15171A]/60"
              style={{ borderColor: `${color}55` }}
            >
              {b}
            </motion.span>
          ))}
        </div>
      </section>

      {/* CHOOSE/AVOID */}
      <section className="px-5 md:px-12 max-w-7xl mx-auto py-16 border-t border-white/5" data-testid="choose-avoid">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          <div className="border-l-2 pl-6" style={{ borderColor: color }}>
            <p className="overline text-[#C17F59] mb-3">Choose This If</p>
            <p className="font-display text-2xl leading-snug">{gin.choose_if}</p>
          </div>
          <div className="border-l-2 border-white/30 pl-6">
            <p className="overline text-white/60 mb-3">Avoid This If</p>
            <p className="font-display text-2xl leading-snug text-white/70">{gin.avoid_if}</p>
          </div>
        </div>
      </section>

      {/* BEST SERVES */}
      <section className="px-5 md:px-12 max-w-7xl mx-auto py-16 border-t border-white/5" data-testid="best-serves-section">
        <p className="overline text-[#C17F59] mb-2">Best Served As</p>
        <h2 className="font-display text-4xl mb-8">How we'd pour it.</h2>
        <div className="flex flex-wrap gap-2">
          {gin.best_serves.map((s) => (
            <span key={s} className="bg-[#15171A] border border-white/10 px-4 py-3 text-sm tracking-wide">{s}</span>
          ))}
        </div>
      </section>

      {/* BENO NOTE */}
      <section className="px-5 md:px-12 max-w-7xl mx-auto py-16 border-t border-white/5" data-testid="beno-note">
        <p className="overline text-[#C17F59] mb-2">BENO Note</p>
        <p className="font-display text-2xl md:text-3xl leading-snug max-w-3xl italic">"{gin.beno_note}"</p>
      </section>

      {/* THE STORY (enrichment-approved) */}
      {gin.story && (
        <section className="px-5 md:px-12 max-w-7xl mx-auto py-16 border-t border-white/5" data-testid="story-section">
          <p className="overline text-[#C17F59] mb-2">The Story</p>
          <h2 className="font-display text-4xl mb-6">Where it comes from.</h2>
          <p className="text-white/80 leading-relaxed font-display text-xl max-w-3xl">{gin.story}</p>
        </section>
      )}

      {/* HOW IT'S MADE (enrichment-approved) */}
      {gin.production_notes && (
        <section className="px-5 md:px-12 max-w-7xl mx-auto py-16 border-t border-white/5" data-testid="production-section">
          <p className="overline text-[#C17F59] mb-2">How It's Made</p>
          <h2 className="font-display text-4xl mb-6">The still, the method, the choices.</h2>
          <p className="text-white/80 leading-relaxed font-display text-xl max-w-3xl">{gin.production_notes}</p>
          {(gin.production_still || gin.production_method || gin.production_water || gin.distillery) && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-8 pt-6 border-t border-white/5">
              {gin.distillery && <Fact label="Distillery" value={gin.distillery} />}
              {gin.production_still && <Fact label="Still" value={gin.production_still} />}
              {gin.production_method && <Fact label="Method" value={gin.production_method} />}
              {gin.production_water && <Fact label="Water" value={gin.production_water} />}
            </div>
          )}
        </section>
      )}

      {/* WHY THIS BOTTLE MATTERS (enrichment-approved trivia) */}
      {gin.trivia && (
        <section className="px-5 md:px-12 max-w-7xl mx-auto py-16 border-t border-white/5" data-testid="trivia-section">
          <p className="overline text-[#C17F59] mb-2">Why This Bottle Matters</p>
          <div className="relative max-w-3xl">
            <span className="absolute -top-2 -left-2 font-display text-7xl text-[#C17F59]/30 leading-none">"</span>
            <p className="font-display text-2xl md:text-3xl leading-snug italic pl-8">{gin.trivia}</p>
          </div>
        </section>
      )}

      {/* AWARDS (enrichment-approved) */}
      {Array.isArray(gin.awards) && gin.awards.length > 0 && (
        <section className="px-5 md:px-12 max-w-7xl mx-auto py-16 border-t border-white/5" data-testid="awards-section">
          <p className="overline text-[#C17F59] mb-2">Recognition</p>
          <h2 className="font-display text-4xl mb-8">Pinned to the wall.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {gin.awards.map((a, i) => (
              <div key={i} className="border border-white/8 bg-[#15171A] p-4 flex items-start gap-3">
                <span className="font-display text-3xl text-[#C17F59] tabular-nums shrink-0">{a.year || "·"}</span>
                <div>
                  <p className="font-display text-lg leading-tight">{a.name}</p>
                  {a.category && <p className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-1">{a.category}</p>}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* WHY IT MATTERS (enrichment-approved essay) */}
      {gin.why_it_matters && (
        <section className="px-5 md:px-12 max-w-7xl mx-auto py-20 border-t border-white/5 relative" data-testid="why-matters-section">
          <div className="absolute -top-10 left-12 font-display text-9xl text-[#C17F59]/10 leading-none select-none pointer-events-none">"</div>
          <p className="overline text-[#C17F59] mb-3">Why It Matters</p>
          <div className="max-w-3xl">
            {gin.why_it_matters.split(/\n\n+/).map((para, i) => (
              <p key={i} className="font-display text-lg md:text-xl leading-relaxed text-white/80 mb-5 last:mb-0">
                {para.trim()}
              </p>
            ))}
          </div>
          <p className="font-hand text-xl text-[#D89A74] -rotate-1 mt-6">— from the BENO editor's desk.</p>
        </section>
      )}

      {/* RELATED */}
      <section className="px-5 md:px-12 max-w-7xl mx-auto py-16 border-t border-white/5 space-y-12" data-testid="related-section">
        <div>
          <p className="overline text-[#C17F59] mb-2">Similar Gins</p>
          <h2 className="font-display text-4xl mb-6">Same DNA, different stories.</h2>
          <RelatedGrid items={related.similar.slice(0, 4)} />
        </div>
        {related.upgrade.length > 0 && (
          <div>
            <p className="overline text-[#C17F59] mb-2">Upgrade Path</p>
            <h2 className="font-display text-3xl mb-6">Same mood, higher price.</h2>
            <RelatedGrid items={related.upgrade.slice(0, 3)} />
          </div>
        )}
        {related.wildcard.length > 0 && (
          <div>
            <p className="overline text-[#C17F59] mb-2">Wildcard</p>
            <h2 className="font-display text-3xl mb-6">A leap, but a fun one.</h2>
            <RelatedGrid items={related.wildcard.slice(0, 3)} />
          </div>
        )}
      </section>
    </div>
  );
}

function RelatedGrid({ items }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {items.map((g) => (
        <Link key={g.id} to={`/gin/${g.id}`}
              className="group bg-[#15171A] border border-white/5 hover:border-[#C17F59]/40 p-5 flex flex-col items-center text-center transition-colors"
              data-testid={`related-${g.id}`}>
          <BottleSilhouette category={g.category} size={70} />
          <p className="font-display text-lg mt-2 leading-tight">{g.name}</p>
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">{g.country} · ₹{g.price}</p>
        </Link>
      ))}
    </div>
  );
}

function Fact({ label, value }) {
  return (
    <div>
      <p className="overline text-white/40 mb-1">{label}</p>
      <p className="font-display text-lg leading-snug">{value}</p>
    </div>
  );
}

function CompareButton({ ginId }) {
  const [added, setAdded] = useState(false);
  useEffect(() => {
    try { setAdded(JSON.parse(localStorage.getItem("beno-compare") || "[]").includes(ginId)); } catch { /* noop */ }
  }, [ginId]);
  const toggle = () => {
    try {
      const list = JSON.parse(localStorage.getItem("beno-compare") || "[]");
      const next = list.includes(ginId) ? list.filter((i) => i !== ginId) : list.length >= 3 ? [list[1], list[2], ginId] : [...list, ginId];
      localStorage.setItem("beno-compare", JSON.stringify(next));
      setAdded(next.includes(ginId));
    } catch { /* noop */ }
  };
  return (
    <button onClick={toggle}
            className={`inline-flex items-center gap-2 px-4 py-3 text-[11px] uppercase tracking-[0.25em] transition-colors border ${
              added ? "bg-[#C17F59] text-[#0E0F0C] border-[#C17F59]" : "border-white/15 hover:border-[#C17F59] hover:text-[#C17F59]"
            }`}
            data-testid="detail-compare-button">
      <GitCompareArrows size={14} />
      {added ? "Added to Compare" : "Add to Compare"}
    </button>
  );
}
